import { messageListAriaLabel } from './MailListContent.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import Draggable from 'owa-dnd/lib/components/Draggable';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import getMailListRowDragData from 'owa-mail-list-store/lib//utils/getMailListRowDragData';
import getMailListTableProps from '../utils/getMailListTableProps';
import { shouldShowListView, isSingleLineListView } from 'owa-mail-layout';
import { lazyLoadMoreInTable, getCanTableLoadMore } from 'owa-mail-triage-table-load-extra';
import LoadMoreListView, {
    LoadMoreListViewHandle,
} from 'owa-loadmore-listview/lib/components/LoadMoreListView';
import MailListGroupHeader from './MailListGroupHeader';
import MailListItemDataProvider from './MailListItemDataProvider';
import MailListLoading from './MailListLoading';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { MessageAdList } from 'owa-mail-messagead-list-view';
import { observer } from 'mobx-react-lite';
import { lazyResetFocus, tabIndex } from 'owa-mail-focus-manager';
import {
    getMailListGroupHeader,
    getMailListGroupHeaderGenerator,
} from 'owa-mail-list-group-headers';
import {
    getMailListDragPreview,
    MAIL_ITEM_DRAG_XOFFSET,
    MAIL_ITEM_DRAG_YOFFSET,
} from '../utils/mailListDragUtil';
import {
    getFocusedFilterForTable,
    getIsEverythingSelectedInTable,
    listViewStore,
    TableView,
    getItemsOrConversationsSelectedText,
    getIsSearchTableShown,
    TableQueryType,
} from 'owa-mail-list-store';
import { GetStartedListInListView } from 'owa-getstarted';
import { AnswersContainer, isAnswerFeatureEnabled } from 'owa-mail-search';
import { useLazyKeydownHandler } from 'owa-hotkeys';
import {
    lazyLogMailListItemPreloadData,
    lazySetupMailListContentKeys,
    lazySetupTriageActionKeys,
    lazyUpdateMailItemHeights,
    lazyUpdateMailItemHeightsThrottled,
} from './lazy/lazyListViewModule';
import { useMergedRefs } from 'owa-react-hooks/lib/useMergedRefs';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';
import { RollupContainer } from 'owa-mail-rollup';
import { hasSenderImageOffInFullView, MailListTableProps } from 'owa-mail-list-item-shared';
import updateLastIndexToRender from '../utils/updateLastIndexToRender';
import { getDensityModeString } from 'owa-fabric-theme';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterSearchPrompt } from 'owa-mail-search-list-view';
import { mailListItemAnimationStore } from 'owa-mail-list-item-animation-store';
import { getStore as getMailSearchStore } from 'owa-mail-search/lib/store/store';
import { runListAnimation } from '../utils/runListAnimation';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { setItem, getItem } from 'owa-local-storage';
import { getBrowserWidth } from 'owa-config';

import styles from './MailList.scss';
import classNames from 'classnames';

const BOOTSTRAP_MAILLISTITEM_VIEW_MODE = 'bootstrapMailListItemViewMode';
const BOOTSTRAP_MAILLISTITEM_WINDOW_WIDTH = 'bootstrapMailListItemWindowWidth';
const BOOTSTRAP_MAILLISTITEM_VIEW_SWAP_SETTING = 'bootstrapMailListItemViewSwapSetting';

export interface MailListContentProps {
    isFocusOnMailList: () => boolean;
    isFocusInMailList: () => boolean;
    tableViewId: string;
    onScroll?: (ev?: any) => void;
    shouldShowMessageAdList: boolean;
    shouldShowGetStartedList: boolean;
    styleSelectorAsPerUserSettings: string;
}

function useFocusManager(
    props: Pick<MailListContentProps, 'isFocusInMailList' | 'isFocusOnMailList'>,
    trySetFocus: () => boolean,
    tableView: TableView
) {
    React.useEffect(() => {
        // If the listview loads without cached content, only MailList is displayed.
        // When the mail list content gets rendered layer, it needs to transfer focus from MailList to MailListContent
        // to support keyboard navigation.
        window.requestAnimationFrame(() => {
            if (props.isFocusOnMailList()) {
                trySetFocus();
            }
        });
    });

    React.useEffect(() => {
        return () => {
            if (props.isFocusInMailList()) {
                // Reset focus such that it can be restored when component is updated
                lazyResetFocus.importAndExecute();
            }
        };
    }, []);

    const currentShowListPane = shouldShowListView();
    const lastShowListPane = React.useRef<boolean>();
    const lastTableViewId = React.useRef<string>();
    React.useEffect(() => {
        // When list pane changes from shown to hidden, it means we have gone into immersive reading pane and we need to
        // save current scroll position for if user exits and goes back to same list pane (in same table). When user
        // exits immersive reading pane and returns to the same table / previously shown list pane which changes list pane
        // from hidden to shown, we need to reset focus
        if (
            lastShowListPane.current !== undefined &&
            lastShowListPane.current !== currentShowListPane &&
            lastTableViewId.current === tableView.id
        ) {
            lazyResetFocus.importAndExecute();
        }

        lastShowListPane.current = currentShowListPane;
        lastTableViewId.current = tableView.id;
    }, [currentShowListPane]);
}

function useMemoizedMailListTableProps(tableViewId: string) {
    // Because the value depends on computed properties, we can't simply use React.useMemo()
    // with just tableViewId as a dependency. However, we do need to use useMemo to keep the
    // object reference the same
    return React.useMemo(() => getMailListTableProps(tableViewId), [
        ...Object.values(getMailListTableProps(tableViewId)),
    ]);
}

export default observer(
    function MailListContent(props: MailListContentProps, ref: React.Ref<HTMLDivElement>) {
        const mailListViewContent = React.useRef<HTMLDivElement>();
        const containerRef = useMergedRefs(mailListViewContent, ref);
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (135,15): Type 'MutableRefObject<LoadMoreDataZone | undefined>' is not assignable to type 'MutableRefObject<LoadMoreDataZone>'.
        // @ts-expect-error
        const listView: React.MutableRefObject<LoadMoreListViewHandle> = React.useRef<LoadMoreListViewHandle>();
        const propagateScrollingTask = React.useRef<number>();
        const propagateResizeTask = React.useRef<number>();

        const tableView = listViewStore.tableViews.get(props.tableViewId);
        const mailListTableProps = useMemoizedMailListTableProps(props.tableViewId);
        const mailListGroupHeaderGenerator = useComputedValue(
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (145,51): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
            // @ts-expect-error
            () => getMailListGroupHeaderGenerator(tableView),
            [tableView]
        );
        const densityModeString = getDensityModeString();

        const trySetFocus = React.useCallback((): boolean => {
            if (mailListViewContent.current) {
                mailListViewContent.current.focus();

                return true;
            }
            return false;
        }, []);

        // Log correctness of the preload optimization after initial render.
        React.useEffect(() => {
            /**
             * Determine params here so the proper state is used (as the util is
             * lazy and the state will have been updated already by the time it's
             * called).
             */
            const params = {
                previousSessionBrowserWidth: parseInt(
                    getItem(window, BOOTSTRAP_MAILLISTITEM_WINDOW_WIDTH) ?? '0'
                ),
                currentSessionBrowserWidth: getBrowserWidth(),
                previousSessionViewMode:
                    getItem(window, BOOTSTRAP_MAILLISTITEM_VIEW_MODE) ?? 'none',
                currentSessionViewMode: mailListTableProps.isSingleLine ? 'slv' : '3col',
                previousSessionViewSwapSetting:
                    getItem(window, BOOTSTRAP_MAILLISTITEM_VIEW_SWAP_SETTING) ?? 'none',
            };

            lazyLogMailListItemPreloadData.importAndExecute(params);
        }, []);

        /**
         * Stores user's view and browser width in local storage to be used on
         * next boot so the proper component is preloaded.
         */
        React.useEffect(() => {
            if (mailListTableProps.isSingleLine) {
                setItem(window, BOOTSTRAP_MAILLISTITEM_VIEW_MODE, 'slv');
            } else {
                setItem(window, BOOTSTRAP_MAILLISTITEM_VIEW_MODE, '3col');
            }

            setItem(window, BOOTSTRAP_MAILLISTITEM_WINDOW_WIDTH, getBrowserWidth().toString());
        }, [mailListTableProps.isSingleLine]);

        useLazyKeydownHandler(
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (163,13): Argument of type 'MutableRefObject<HTMLDivElement | undefined>' is not assignable to parameter of type 'RefObject<HTMLElement | null>'.
            // @ts-expect-error
            mailListViewContent,
            lazySetupMailListContentKeys.importAndExecute,
            props.tableViewId
        );

        useLazyKeydownHandler(
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (172,13): Argument of type 'null' is not assignable to parameter of type 'RefObject<HTMLElement | null> | undefined'.
            // @ts-expect-error
            null /* applies handler to document.body */,
            lazySetupTriageActionKeys.importAndExecute,
            props.tableViewId
        );

        // Determine last index to render on folder switch, list view change, or density mode change
        React.useEffect(() => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (182,37): Object is possibly 'undefined'.
            // @ts-expect-error
            updateLastIndexToRender(tableView.rowKeys, tableView);
        }, [
            isSingleLineListView(),
            densityModeString,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (189,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.tableQuery.folderId,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (193,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.rowKeys.length,
        ]);

        React.useEffect(() => {
            if (mailListItemAnimationStore.animationRowIds) {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345(201,44): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                // @ts-expect-error
                runListAnimation(listView, tableView);
            }
        }, [mailListItemAnimationStore.animationRowIds]);

        // Determine last index to render on window resize. Propagate resize task to avoid calling too many times
        useWindowEvent(
            'resize',
            () => {
                if (propagateResizeTask.current) {
                    window.clearTimeout(propagateResizeTask.current);
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2322 (214,21): Type 'null' is not assignable to type 'number | undefined'.
                    // @ts-expect-error
                    propagateResizeTask.current = null;
                }

                propagateResizeTask.current = window.setTimeout(() => {
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2322 (221,21): Type 'null' is not assignable to type 'number | undefined'.
                    // @ts-expect-error
                    propagateResizeTask.current = null;
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2532 (225,45): Object is possibly 'undefined'.
                    // @ts-expect-error
                    updateLastIndexToRender(tableView.rowKeys, tableView);
                }, 150);
            },
            []
        );

        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (234,45): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
        // @ts-expect-error
        useFocusManager(props, trySetFocus, tableView);

        const getCanCurrentTableLoadMore = React.useCallback((): boolean => {
            return isFeatureEnabled('mon-tri-listViewAnimation') &&
                mailListItemAnimationStore.animationRowIds?.size > 0 // When animating we dont want to load more rows because it can effect the position of other items and the layout.
                ? false
                : // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                  // -> Error TS2345 (243,39): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                  // @ts-expect-error
                  getCanTableLoadMore(tableView);
        }, [tableView, mailListItemAnimationStore.animationRowIds]);

        const getAriaLabel = () => {
            let ariaLabel = loc(messageListAriaLabel);

            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (252,48): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
            // @ts-expect-error
            if (getIsEverythingSelectedInTable(tableView) || tableView.selectedRowKeys.size == 0) {
                ariaLabel += ' ' + getItemsOrConversationsSelectedText(props.tableViewId);
            }

            return ariaLabel;
        };

        const onScroll = React.useCallback(
            (event: any, scrollTop: number, offsetHeight: number) => {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2722 (264,17): Cannot invoke an object which is possibly 'undefined'.
                // @ts-expect-error
                props.onScroll(event);
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2532 (268,33): Object is possibly 'undefined'.
                // @ts-expect-error
                const rowKeys = tableView.rowKeys;

                // Virtualization will only kick in after a certain amount of rows have been loaded in.
                // Or immediately if virtualize on table load flight is enabled
                if (isFeatureEnabled('tri-virtualizeLessAggressively')) {
                    lazyUpdateMailItemHeightsThrottled.importAndExecute(
                        rowKeys,
                        scrollTop,
                        offsetHeight
                    );
                } else {
                    if (propagateScrollingTask.current) {
                        window.clearTimeout(propagateScrollingTask.current);
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2322 (284,25): Type 'null' is not assignable to type 'number | undefined'.
                        // @ts-expect-error
                        propagateScrollingTask.current = null;
                    }

                    propagateScrollingTask.current = window.setTimeout(() => {
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2322 (291,25): Type 'null' is not assignable to type 'number | undefined'.
                        // @ts-expect-error
                        propagateScrollingTask.current = null;
                        lazyUpdateMailItemHeights.importAndExecute(
                            rowKeys,
                            scrollTop,
                            offsetHeight
                        );
                    }, 100);
                }
            },
            [props.onScroll, tableView]
        );

        /**
         * Clear tabindex of container such that container is not in a sequential
         * keyboard navigation when an item is selected in listview
         */
        const resetListViewContentTabIndex = React.useCallback(() => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (311,17): Object is possibly 'undefined'.
            // @ts-expect-error
            if (mailListViewContent.current.getAttribute('tabindex') == tabIndex.sequentialIndex) {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2532 (315,17): Object is possibly 'undefined'.
                // @ts-expect-error
                mailListViewContent.current.setAttribute('tabindex', tabIndex.nonSequentialIndex);
            }
        }, []);

        const onLoadMoreRows = React.useCallback(() => {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (323,50): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
            // @ts-expect-error
            lazyLoadMoreInTable.importAndExecute(tableView);
        }, [tableView]);

        const canDrag = React.useCallback(() => {
            return mailListTableProps.canDragFromTable;
        }, [mailListTableProps.canDragFromTable]);

        const isDraggable = React.useCallback(() => {
            return getMailListRowDragData().rowKeys.length > 0 && mailListTableProps.isDraggable;
        }, [mailListTableProps.isDraggable]);

        const isAnswerAvailable = getMailSearchStore().isAnswerAvailable;

        const createMailListItem = React.useCallback(
            (rowKey: string, itemIndex: number, listProps: MailListTableProps) => {
                // The unique key must be provided to the outermost element, otherwise React will
                // default to using the list index. This would be VERY bad as any row delete/additions
                // will cause a re-render
                return (
                    <React.Fragment key={`item_${rowKey}`}>
                        <MailListItemDataProvider
                            rowKey={rowKey}
                            mailListTableProps={listProps}
                            resetListViewContentTabIndex={resetListViewContentTabIndex}
                            isFocusInMailList={props.isFocusInMailList}
                            styleSelectorAsPerUserSettings={props.styleSelectorAsPerUserSettings}
                        />
                        {isAnswerFeatureEnabled() &&
                            (isFeatureEnabled('sea-enableMultipleAnswers') ||
                                isFeatureEnabled('sea-searchTabs-allTab')) &&
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (355,29): Object is possibly 'undefined'.
                            // @ts-expect-error
                            tableView.tableQuery.type === TableQueryType.Search &&
                            itemIndex !== 0 && (
                                <AnswersContainer
                                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                    // -> Error TS2532 (361,49): Object is possibly 'undefined'.
                                    // @ts-expect-error
                                    tableQuery={tableView.tableQuery}
                                    itemIndex={itemIndex}></AnswersContainer>
                            )}
                        {/* Show prompt to advanced search after the 18th item, iff there are at least 25 items */}
                        {isFeatureEnabled('sea-listView-promptAdvancedSearch') &&
                            getIsSearchTableShown() &&
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (370,29): Object is possibly 'undefined'.
                            // @ts-expect-error
                            tableView.rowKeys.length >= 25 &&
                            itemIndex == 17 && (
                                <FilterSearchPrompt
                                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                    // -> Error TS2532 (376,47): Object is possibly 'undefined'.
                                    // @ts-expect-error
                                    rowCount={tableView.rowKeys.length}
                                    isInline={true}
                                />
                            )}
                    </React.Fragment>
                );
            },
            [
                resetListViewContentTabIndex,
                props.isFocusInMailList,
                props.styleSelectorAsPerUserSettings,
                isAnswerAvailable,
                getIsSearchTableShown,
            ]
        );

        const createHeader = React.useCallback(
            (previousRowKey: string, currentRowKey: string) => {
                const headers: JSX.Element[] = [];
                const headerText = getMailListGroupHeader(
                    previousRowKey,
                    currentRowKey,
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2345 (401,21): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                    // @ts-expect-error
                    tableView,
                    mailListGroupHeaderGenerator
                );

                if (headerText) {
                    headers.push(
                        <MailListGroupHeader
                            key={`gh_${headerText}`}
                            headerText={headerText}
                            groupHeaderStylesAsPerUserSettings={
                                props.styleSelectorAsPerUserSettings
                            }
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2345 (416,86): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                            // @ts-expect-error
                            hasSenderImageOffInFullView={hasSenderImageOffInFullView(tableView)}
                        />
                    );
                }

                headers.push(
                    <RollupContainer
                        key={`rollup_${currentRowKey}`}
                        previousRowKey={previousRowKey}
                        currentRowKey={currentRowKey}
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2532 (429,35): Object is possibly 'undefined'.
                        // @ts-expect-error
                        folderId={tableView.tableQuery.folderId}
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2345 (433,69): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                        // @ts-expect-error
                        focusedViewFilter={getFocusedFilterForTable(tableView)}
                        styleSelectorAsPerUserSettings={props.styleSelectorAsPerUserSettings}
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2322 (438,25): Type 'TableView | undefined' is not assignable to type 'TableView'.
                        // @ts-expect-error
                        tableView={tableView}
                    />
                );

                return <React.Fragment key={`header_${currentRowKey}`}>{headers}</React.Fragment>;
            },
            [
                mailListGroupHeaderGenerator,
                tableView,
                props.styleSelectorAsPerUserSettings,
                props.tableViewId,
            ]
        );

        const loadingSpinner = React.useMemo((): JSX.Element => {
            // Do not render the loading spinner if the table cannot load more. If the table is in loading state but cannot load more,
            // it indicates it's doing a reload in which case we don't want to show the load more spinner.
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (458,13): Type 'Element | null' is not assignable to type 'Element'.
            // @ts-expect-error
            return getCanCurrentTableLoadMore() ? <MailListLoading /> : null;
        }, [getCanCurrentTableLoadMore()]);

        const SpinnerPlaceholder = React.useCallback((): JSX.Element => {
            // Do not show the placeholder if we're loading and showing the spinner, or this table
            // cannot load more which means the spinner won't ever appear.
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (467,17): Object is possibly 'undefined'.
            // @ts-expect-error
            if (tableView.isLoading || !getCanCurrentTableLoadMore()) {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (471,17): Type 'null' is not assignable to type 'Element'.
                // @ts-expect-error
                return null;
            }

            // Create an element to render after all rows that take up the same space as the loading indicator,
            // so that the height of the scroll region doesn't change when loading indicator appears
            return <div className={styles.spinnerPlaceHolder} />;
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (480,13): Object is possibly 'undefined'.
            // @ts-expect-error
        }, [tableView.isLoading, getCanCurrentTableLoadMore]);

        const FilterPrompt = React.useCallback((): JSX.Element => {
            // Only show prompt for filter if the end of a search table is being shown
            if (
                !isFeatureEnabled('sea-listView-promptAdvancedSearch') ||
                !getIsSearchTableShown() ||
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2532 (490,17): Object is possibly 'undefined'.
                // @ts-expect-error
                tableView.isLoading ||
                getCanCurrentTableLoadMore()
            ) {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (496,17): Type 'null' is not assignable to type 'Element'.
                // @ts-expect-error
                return null;
            }
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (501,50): Object is possibly 'undefined'.
            // @ts-expect-error
            return <FilterSearchPrompt rowCount={tableView.rowKeys.length} isInline={false} />;
        }, [
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (506,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.rowKeys.length,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (510,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.isLoading,
            getCanCurrentTableLoadMore,
            getIsSearchTableShown,
        ]);

        const preRowsComponent = React.useCallback(() => {
            return (
                <>
                    {isAnswerFeatureEnabled() &&
                        !(
                            isFeatureEnabled('sea-enableMultipleAnswers') ||
                            isFeatureEnabled('sea-searchTabs-allTab')
                        ) && (
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (522,55): Object is possibly 'undefined'.
                            // @ts-expect-error
                            <AnswersContainer tableQuery={tableView.tableQuery} />
                        )}
                    {isAnswerFeatureEnabled() &&
                        (isFeatureEnabled('sea-enableMultipleAnswers') ||
                            isFeatureEnabled('sea-searchTabs-allTab')) && (
                            <AnswersContainer
                                itemIndex={0}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2532 (530,41): Object is possibly 'undefined'.
                                // @ts-expect-error
                                tableQuery={tableView.tableQuery}></AnswersContainer>
                        )}
                    {props.shouldShowMessageAdList && (
                        <MessageAdList
                            loadInOtherPivot={
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2345 (538,58): Argument of type 'TableView | undefined' is not assignable to parameter of type 'TableView'.
                                // @ts-expect-error
                                getFocusedFilterForTable(tableView) == FocusedViewFilter.Other
                            }
                            loadInEmptyPivot={false}
                            loadInItemView={
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2532 (545,33): Object is possibly 'undefined'.
                                // @ts-expect-error
                                tableView.tableQuery.listViewType == ReactListViewType.Message
                            }
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2322 (550,29): Type 'TableView | undefined' is not assignable to type 'TableView'.
                            // @ts-expect-error
                            tableView={tableView}
                        />
                    )}
                </>
            );
        }, [
            tableView,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (560,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.tableQuery,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (564,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.id,
            props.shouldShowMessageAdList,
            isAnswerAvailable,
        ]);

        const postRowsComponent = React.useCallback(() => {
            return (
                /* For cases where there are more answers to render than mail search results
                    render all the remaining answers together at the end */
                <>
                    {isAnswerFeatureEnabled() &&
                        (isFeatureEnabled('sea-enableMultipleAnswers') ||
                            isFeatureEnabled('sea-searchTabs-allTab')) &&
                        isAnswerAvailable && (
                            <AnswersContainer
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2532 (581,45): Object is possibly 'undefined'.
                                // @ts-expect-error
                                tableQuery={tableView.tableQuery}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2532 (585,47): Object is possibly 'undefined'.
                                // @ts-expect-error
                                totalRowKeys={tableView.rowKeys.length}></AnswersContainer>
                        )}
                    {props.shouldShowGetStartedList && (
                        <div className={styles.getStartedContainer}>
                            {/*// Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2322 (592,55): Type '{ rowCount: number; }' is not assignable to type 'IntrinsicAttributes & Pick<PropsWithChildren<object & RefAttributes<{}>>, "children" | "key"> & RefAttributes<{}>'.
                            // @ts-expect-error */}
                            <GetStartedListInListView rowCount={tableView.rowKeys.length} />
                        </div>
                    )}
                    <SpinnerPlaceholder />
                    <FilterPrompt />
                </>
            );
        }, [
            props.shouldShowGetStartedList,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (604,13): Object is possibly 'undefined'.
            // @ts-expect-error
            tableView.rowKeys.length,
            SpinnerPlaceholder,
            FilterPrompt,
            isAnswerAvailable,
        ]);

        const renderListViewContent = (): JSX.Element => {
            const listProps = mailListTableProps;

            const loadMoreListView = (
                <div role="region" className={styles.wrapperDiv}>
                    <div
                        tabIndex={0}
                        role="listbox"
                        aria-multiselectable={true}
                        aria-label={getAriaLabel()}
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2322 (623,25): Type '(value: HTMLDivElement | undefined) => void' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
                        // @ts-expect-error
                        ref={containerRef}
                        className={styles.wrapperDiv}>
                        <LoadMoreListView
                            ref={listView}
                            className={classNames('customScrollBar', styles.outerScrollContainer)}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (631,43): Object is possibly 'undefined'.
                            // @ts-expect-error
                            dataSourceId={tableView.id}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (635,38): Object is possibly 'undefined'.
                            // @ts-expect-error
                            itemIds={tableView.rowKeys}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2322 (639,29): Type '(event: any, scrollTop: number, offsetHeight: number) => void' is not assignable to type '(event: any, scrollTop?: number | undefined, offsetHeight?: number | undefined) => void'.
                            // @ts-expect-error
                            onScroll={onScroll}
                            onRenderRow={createMailListItem}
                            onRenderHeader={createHeader}
                            onLoadMoreRows={onLoadMoreRows}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (646,51): Object is possibly 'undefined'.
                            // @ts-expect-error
                            isLoadRowsInProgress={tableView.isLoading}
                            getCanLoadMore={getCanCurrentTableLoadMore}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (651,49): Object is possibly 'undefined'.
                            // @ts-expect-error
                            currentLoadedIndex={tableView.currentLoadedIndex}
                            guardPageCount={3}
                            loadingComponent={loadingSpinner}
                            rowWrapperClass={classNames(
                                styles.rowWrapper,
                                isFeatureEnabled('mon-tri-listViewAnimation')
                                    ? styles.animationZindex
                                    : undefined
                            )}
                            PreRowsComponent={preRowsComponent}
                            PostRowsComponent={postRowsComponent}
                            listProps={listProps}
                            isAnimationRunning={
                                mailListItemAnimationStore.animationRowIds?.size > 0
                            }
                        />
                    </div>
                </div>
            );

            return loadMoreListView;
        };

        return (
            <Draggable
                canDrag={canDrag}
                isDraggable={isDraggable}
                getDragData={getMailListRowDragData}
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (682,17): Type '(dragData: MailListRowDragData | MailListItemPartDragData) => HTMLElement' is not assignable to type '(dragData: DragData) => HTMLElement'.
                // @ts-expect-error
                getDragPreview={getMailListDragPreview}
                xOffset={MAIL_ITEM_DRAG_XOFFSET}
                yOffset={MAIL_ITEM_DRAG_YOFFSET}
                classNames={styles.draggableDiv}>
                {renderListViewContent()}
            </Draggable>
        );
    },
    { forwardRef: true }
);
